import { OptionType, Security, SecurityCategory_CategoryCode, Segment } from '../../proto/generated/security'
import type { OptionSeriesDef } from './types'

export const futureInstrumentFor = (cashCategory: SecurityCategory_CategoryCode | undefined) =>
    cashCategory === SecurityCategory_CategoryCode.STOCKS ? 'FUTSTK' : cashCategory === SecurityCategory_CategoryCode.CURRENCY ? 'FUTCUR' : 'FUTIDX'

export const optionInstrumentFor = (cashCategory: SecurityCategory_CategoryCode | undefined) =>
    cashCategory === SecurityCategory_CategoryCode.STOCKS ? 'OPTSTK' : cashCategory === SecurityCategory_CategoryCode.CURRENCY ? 'OPTCUR' : 'OPTIDX'

/** Builds the option `Security` exactly as the old eager expansion did, but only when asked for. */
export function buildOptionSecurity(
    def: OptionSeriesDef,
    underlying: Security,
    strike: number,
    optionType: OptionType.CE | OptionType.PE,
): Security {
    const side = optionType === OptionType.CE ? 'CE' : 'PE'
    const strikeLabel = (strike / (def.priceMultiplier ?? 100)).toFixed(0)
    return {
        ...underlying,
        priceMultiplier: def.priceMultiplier,
        lotSize: def.lotSize,
        volumeFreezeQuantity: def.volumeFreezeQuantity,
        tickSize: def.tickSize,
        expiry: def.expiry,
        segment: Segment.FO,
        series: 'XX',
        symbol: `${def.symbolPrefix} ${strikeLabel} ${side}`,
        displayName: `${def.displayNamePrefix} ${strikeLabel} ${side}`,
        code: `${def.securityCodePrefix}${strike}_${side}`,
        categoryCode: SecurityCategory_CategoryCode.OPTIONS,
        instrument: optionInstrumentFor(underlying.categoryCode),
        exchange: def.exchange,
        underlyingSecurityCode: underlying.code,
        strikePrice: strike,
        optionType,
        hasFo: false,
        derived: false,
        exchangeSymbol: underlying.exchangeSymbol,
        name: def.name,
        partOfCAS: false,
        tradeable: true,
    }
}

export interface ParsedOptionKey {
    seriesId: string
    strike: number
    optionType: OptionType.CE | OptionType.PE
}

const OPTION_KEY_REGEX = /^([A-Za-z]+):(.+)_(CE|PE)$/

/** `NSE:NIFTY_50_14_NOV_24500_CE` → series `NSE:NIFTY_50_14_NOV_`, strike 24500, CE. */
export function parseOptionKey(key: string): ParsedOptionKey | null {
    const match = OPTION_KEY_REGEX.exec(key)
    if (!match) return null
    const [, exchange, base, side] = match
    const split = base.lastIndexOf('_')
    const strike = Number(base.slice(split + 1))
    if (!Number.isFinite(strike)) return null
    return {
        seriesId: `${exchange}:${base.slice(0, split + 1)}`,
        strike,
        optionType: side === 'CE' ? OptionType.CE : OptionType.PE,
    }
}
